import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const INTEGRATION_PATH = '/api/v1/integrations/notes/{session_id}';

export default function CustomApi() {
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [selectedFields, setSelectedFields] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);

  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldDesc, setNewFieldDesc] = useState('');
  const [openNewField, setOpenNewField] = useState(false);
  const [addingField, setAddingField] = useState(false);
  const [deletingFieldId, setDeletingFieldId] = useState(null);

  const [openApiKey, setOpenApiKey] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    fetchFields();
    fetchApiKeys();
  }, []);

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/admin/custom-fields`);
      setFields(response.data.fields || []);
    } catch (error) {
      toast.error('Failed to load custom fields');
    } finally {
      setFieldsLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/admin/custom-api/keys`);
      setApiKeys(response.data.keys || []);
    } catch (error) {
      console.error('Failed to fetch API keys');
    }
  };

  const handleToggleField = (fieldKey) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((k) => k !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleAddCustomField = async () => {
    if (addingField) return;
    if (!newFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }
    setAddingField(true);
    try {
      await axios.post(`${API_URL}/api/v1/admin/custom-fields`, {
        name: newFieldName.trim(),
        description: newFieldDesc.trim() || null,
      });
      setNewFieldName('');
      setNewFieldDesc('');
      setOpenNewField(false);
      await fetchFields();
      toast.success('Custom field added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add custom field');
    } finally {
      setAddingField(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (deletingFieldId) return;
    if (!window.confirm(
      `Remove the "${field.label}" field?\n\n` +
      `New notes will no longer extract it. Existing notes keep any value already stored.`
    )) return;
    setDeletingFieldId(field.id);
    try {
      await axios.delete(`${API_URL}/api/v1/admin/custom-fields/${field.id}`);
      setSelectedFields((prev) => prev.filter((k) => k !== field.field_name));
      await fetchFields();
      toast.success('Field removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove field');
    } finally {
      setDeletingFieldId(null);
    }
  };

  const handleGenerateApi = async () => {
    if (selectedFields.length === 0) {
      toast.error('Select at least one field');
      return;
    }
    if (generating) return;
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/v1/admin/custom-api`, {
        fields: selectedFields,
      });
      setGeneratedKey(response.data);
      setOpenApiKey(true);
      fetchApiKeys();
      toast.success('API key generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeKey = async (key) => {
    if (revokingId) return;
    if (!window.confirm('Revoke this API key? Any integration using it will stop working immediately. This cannot be undone.')) return;
    setRevokingId(key.id);
    try {
      await axios.delete(`${API_URL}/api/v1/admin/custom-api/keys/${key.id}`);
      await fetchApiKeys();
      toast.success('API key revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setRevokingId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const labelForKey = (k) => fields.find((f) => f.field_name === k)?.label || k;

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Custom API
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Define what the AI extracts from each visit, then generate scoped API keys for your integrations.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Extraction Fields"
          subheader="The AI extracts these from every visit recording. Select the ones a key may read, then generate it."
          action={
            <Button startIcon={<AddIcon />} onClick={() => setOpenNewField(true)}>
              Add Custom Field
            </Button>
          }
        />
        <CardContent>
          {fieldsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : fields.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No fields yet — add one to start extracting custom data.
            </Typography>
          ) : (
            <FormGroup>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 1 }}>
                {fields.map((field) => (
                  <Box
                    key={field.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'flex-start' }}
                      control={
                        <Checkbox
                          sx={{ pt: 0.25 }}
                          checked={selectedFields.includes(field.field_name)}
                          onChange={() => handleToggleField(field.field_name)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{field.label}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {field.field_name}
                          </Typography>
                          {field.description && (
                            <Typography variant="caption" color="text.secondary">{field.description}</Typography>
                          )}
                        </Box>
                      }
                    />
                    <Tooltip title="Remove field">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deletingFieldId === field.id}
                          onClick={() => handleDeleteField(field)}
                        >
                          {deletingFieldId === field.id
                            ? <CircularProgress size={16} color="inherit" />
                            : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </FormGroup>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <KeyIcon />}
              disabled={generating || selectedFields.length === 0}
              onClick={handleGenerateApi}
            >
              {generating ? 'Generating…' : `Generate API Key (${selectedFields.length} fields)`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Active API Keys */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="API Keys" subheader="Each key reads notes for this clinic only, limited to its selected fields." />
        <CardContent>
          {apiKeys.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              No API keys generated yet
            </Typography>
          ) : (
            <List>
              {apiKeys.map((key) => (
                <ListItem
                  key={key.id}
                  alignItems="flex-start"
                  secondaryAction={
                    <Tooltip title={key.is_active ? 'Revoke' : 'Already revoked'}>
                      <span>
                        <IconButton
                          edge="end"
                          color="error"
                          disabled={!key.is_active || revokingId === key.id}
                          onClick={() => handleRevokeKey(key)}
                        >
                          {revokingId === key.id
                            ? <CircularProgress size={18} color="inherit" />
                            : <DeleteIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <CodeIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontFamily="monospace">
                          {key.endpoint}
                        </Typography>
                        <Chip
                          size="small"
                          label={key.is_active ? 'Active' : 'Revoked'}
                          color={key.is_active ? 'success' : 'default'}
                          variant={key.is_active ? 'filled' : 'outlined'}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }} component="span">
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }} component="span">
                          {(key.fields || []).map((field) => (
                            <Chip key={field} label={labelForKey(field)} size="small" variant="outlined" />
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          Created {key.created_at ? new Date(key.created_at).toLocaleDateString() : '—'}
                          {' · '}
                          Last used {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'never'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Integration docs */}
      <Card>
        <CardHeader title="Using your API" subheader="Authenticate with the X-API-Key header. Responses are scoped to your clinic, and only finalized notes are returned — a draft answers 404." />
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Endpoint</Typography>
          <Card variant="outlined" sx={{ bgcolor: 'grey.50', mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" fontFamily="monospace">
                GET {API_URL}{INTEGRATION_PATH}
              </Typography>
            </CardContent>
          </Card>
          <Typography variant="subtitle2" gutterBottom>Example</Typography>
          <Card sx={{ bgcolor: 'grey.900' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ color: 'grey.300', flexGrow: 1, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                >
                  {`curl -X GET '${API_URL}${INTEGRATION_PATH}' \\\n  -H 'X-API-Key: sk_live_••••••••'`}
                </Typography>
                <IconButton
                  onClick={() => copyToClipboard(`curl -X GET '${API_URL}${INTEGRATION_PATH}' -H 'X-API-Key: YOUR_KEY'`)}
                  sx={{ color: 'grey.400' }}
                >
                  <CopyIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            The response includes the patient summary, SOAP note, ICD-10 codes, and a
            <strong> custom_fields </strong> object containing only the fields the key is scoped to.
          </Typography>
        </CardContent>
      </Card>

      {/* Add Custom Field Dialog */}
      <Dialog open={openNewField} onClose={() => { if (!addingField) setOpenNewField(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Custom Field</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Field Name"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            margin="normal"
            required
            helperText="A readable name, e.g. “Fall Risk”. Stored as a key like fall_risk."
          />
          <TextField
            fullWidth
            label="Extraction Instruction"
            value={newFieldDesc}
            onChange={(e) => setNewFieldDesc(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            helperText="Tell the AI what to extract, e.g. “The patient’s fall risk assessment, if mentioned.”"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewField(false)} disabled={addingField}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddCustomField}
            disabled={addingField || !newFieldName.trim()}
            startIcon={addingField ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {addingField ? 'Adding…' : 'Add Field'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generated API Key Dialog */}
      <Dialog open={openApiKey} onClose={() => setOpenApiKey(false)} maxWidth="md" fullWidth>
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          {generatedKey && (
            <Box>
              <Card sx={{ bgcolor: 'primary.50', mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    API Key
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{ flexGrow: 1, wordBreak: 'break-all' }}
                    >
                      {generatedKey.api_key}
                    </Typography>
                    <IconButton onClick={() => copyToClipboard(generatedKey.api_key)}>
                      <CopyIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Copy this key now. You won't be able to see it again.
                  </Typography>
                </CardContent>
              </Card>

              <Typography variant="subtitle2" gutterBottom>
                cURL Example
              </Typography>
              <Card sx={{ bgcolor: 'grey.900' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{ color: 'grey.300', flexGrow: 1, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {generatedKey.curl_example}
                    </Typography>
                    <IconButton onClick={() => copyToClipboard(generatedKey.curl_example)} sx={{ color: 'grey.400' }}>
                      <CopyIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>Selected Fields:</strong>{' '}
                {(generatedKey.fields || []).map(labelForKey).join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApiKey(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
